<template>
  <v-menu
    v-model="opened"
    @update:modelValue="onOpened"
    :close-on-content-click="false"
    location="bottom"
    transition="slide-y-transition"
    offset="15"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ display, props }"></slot>
    </template>
    <v-card>
      <div class="md:flex">
        <div class="hidden md:inline-block w-200px">
          <v-item-group v-model="selection" :disabled="disabled">
            <v-list :lines="false" density="compact" nav>
              <div v-for="item in shortcuts" :key="item.value">
                <v-item v-slot="{ isSelected, toggle }" :value="item.value">
                  <v-list-item
                    @click="toggle"
                    :key="item.value"
                    :title="item.title"
                    :active="isSelected"
                  >
                    <template #append>
                      <v-icon v-if="isSelected" size="20">check</v-icon>
                    </template>
                  </v-list-item>
                </v-item>
              </div>
            </v-list>
          </v-item-group>
        </div>
        <div class="hidden md:inline-block">
          <v-divider vertical />
        </div>
        <div class="md:hidden pa-4">
          <v-select
            v-model="selection"
            :disabled="disabled"
            :items="shortcuts"
            variant="outlined"
            rounded="lg"
            density="compact"
            hide-details
          />
        </div>
        <v-date-picker v-model="date" hide-header />
      </div>
      <v-divider />
      <div class="px-4 py-2 flex gap-2 justify-end">
        <v-btn @click="close">Cancel</v-btn>
        <v-btn @click="apply" color="primary">Apply</v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";

const shortcuts = [
  { title: "Yesterday", value: "previous-period" },
  { title: "Last week", value: "previous-week" },
  { title: "Previous month", value: "previous-month" },
  { title: "Previous quarter", value: "previous-quarter" },
  { title: "Previous year", value: "previous-year" },
  { title: "No comparison", value: "none" },
];

export default defineComponent({
  name: "MetricsDatePicker",
  props: {
    modelValue: { type: String as PropType<string> },
    disabled: { type: Boolean, default: false },
  },
  setup(props, ctx) {
    const selection = ref(props.modelValue || "yesterday");
    const opened = ref(false);

    const display = computed(() => {
      if (selection.value == "none") {
        return "No comparison";
      }
      if (selection.value) {
        const d = parseMetricsPeriod(selection.value);
        if (d) {
          return d.format("D MMM YYYY");
        }
      }

      return "—";
    });

    const date = computed({
      set: (value: Date | undefined) => {
        const d = dayjs(value);
        selection.value = d.format("YYYY-MM-DD HH:mm:ss");
      },
      get() {
        if (selection.value == "none") {
          return;
        }
        if (selection.value) {
          const d = parseMetricsPeriod(selection.value);
          if (d) {
            return d.toDate();
          }
        }
        return new Date();
      },
    });

    const onOpened = () => {
      if (props.modelValue) {
        selection.value = props.modelValue;
      }
    };

    const close = () => {
      opened.value = false;
    };

    const apply = afterEach(() => {
      const v = selection.value === "none" ? undefined : selection.value;
      ctx.emit("update:modelValue", v);
    }, close);

    return {
      display,
      shortcuts,
      selection,
      opened,
      date,
      onOpened,
      close,
      apply,
    };
  },
});
</script>
